import {
  AddDiscountCodeFailedAction,
  AddDiscountCodeFailedPayload,
  AddDiscountCodeSucceededAction,
  AddDiscountCodeSucceededPayload,
  AddDiscountCodeToCurrentCartsRequestedAction,
  addDiscountCodeFailedType,
  addDiscountCodeRequested,
  addDiscountCodeSucceededType,
  addDiscountCodeToCurrentCartsFailed,
  addDiscountCodeToCurrentCartsSucceeded
} from '../../carts/cartsSlice';
import {
  AddDiscountCodeStatus,
  StateCart,
  StateCartDiscountCode,
  StatusEnvelop
} from '@spa/lib/store/carts/CartsState';
import { put, select } from 'typed-redux-saga';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import generateActionId from '@spa/lib/generateActionId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import waitForSucceededOrFailedAction from '@spa/lib/store/sagas/session/waitForSucceededOrFailedAction';

function* addDiscountCodeToCartWithEntries(
  customOrderType: CustomOrderType,
  code: string
): Generator<
  unknown,
  StatusEnvelop<AddDiscountCodeSucceededPayload, AddDiscountCodeFailedPayload> | null
> {
  const cart: StateCart | null = (yield* select(selectCarts))[customOrderType];
  let result: StatusEnvelop<AddDiscountCodeSucceededPayload, AddDiscountCodeFailedPayload> | null =
    null;
  const redeemedCode: StateCartDiscountCode | undefined = cart?.discountCodes?.find(
    ({ code: discountCode, valid }) => discountCode === code && valid
  );

  if (cart !== null && cart.entries.length > 0 && redeemedCode === undefined) {
    const actionId: number = generateActionId();
    yield* put(
      addDiscountCodeRequested({
        discountCode: code,
        actionId,
        customOrderType
      })
    );

    const waitResult = yield* waitForSucceededOrFailedAction<
      typeof addDiscountCodeSucceededType,
      typeof addDiscountCodeFailedType,
      AddDiscountCodeSucceededAction,
      AddDiscountCodeFailedAction
    >(actionId, addDiscountCodeSucceededType, addDiscountCodeFailedType);

    if (waitResult.type === 'succeeded') {
      const { cart: updatedCart, redemptionStatus } = waitResult.action.payload;
      const effectiveRedemptionStatus =
        redemptionStatus === AddDiscountCodeStatus.APPLIED &&
        updatedCart?.discountCodes?.find((discountCode) => discountCode.code === code)
          ?.hasBenefit === false
          ? AddDiscountCodeStatus.NO_BENEFIT
          : redemptionStatus;

      result = {
        succeeded: true,
        result: {
          ...waitResult.action.payload,
          redemptionStatus: effectiveRedemptionStatus
        }
      };
    } else {
      result = {
        succeeded: false,
        result: waitResult.action.payload
      };
    }
  }
  return result;
}

function* addDiscountCodeToCurrentCartsSaga({
  payload: { requestedActionId, discountCode }
}: AddDiscountCodeToCurrentCartsRequestedAction): Generator {
  const carts = yield* select(selectCarts);
  const hasAnotherActiveCode = Object.values(carts).some((cart) =>
    cart?.discountCodes?.some(({ code, valid }) => valid && code !== discountCode)
  );

  if (hasAnotherActiveCode) {
    yield* put(
      addDiscountCodeToCurrentCartsFailed({
        requestedActionId,
        discountCode,
        redemptionStatus: AddDiscountCodeStatus.MULTIPLE_CODES_NOT_ALLOWED
      })
    );
    return;
  }

  const homeDeliveryCartResult: StatusEnvelop<
    AddDiscountCodeSucceededPayload,
    AddDiscountCodeFailedPayload
  > | null = yield* addDiscountCodeToCartWithEntries(CustomOrderType.HOME_DELIVERY, discountCode);

  const dontApplyFurther =
    homeDeliveryCartResult?.succeeded === true &&
    homeDeliveryCartResult.result.isOneTimeDiscountCode;

  const collectAtStoreResult: StatusEnvelop<
    AddDiscountCodeSucceededPayload,
    AddDiscountCodeFailedPayload
  > | null = dontApplyFurther
    ? null
    : yield* addDiscountCodeToCartWithEntries(CustomOrderType.COLLECT_AT_STORE, discountCode);

  const getOptionalSuccessfulResult = (): AddDiscountCodeSucceededPayload | undefined => {
    const appliedToCollectAtStoreCart =
      collectAtStoreResult?.succeeded === true &&
      collectAtStoreResult.result.redemptionStatus === AddDiscountCodeStatus.APPLIED;

    const appliedToHomeDeliveryCart =
      homeDeliveryCartResult?.succeeded === true &&
      homeDeliveryCartResult.result.redemptionStatus === AddDiscountCodeStatus.APPLIED;

    const hasNoBenefitOnCollectAtStoreCart =
      collectAtStoreResult?.succeeded === true &&
      collectAtStoreResult.result.redemptionStatus === AddDiscountCodeStatus.NO_BENEFIT;

    const hasNoBenefitOnHomeDeliveryCart =
      homeDeliveryCartResult?.succeeded === true &&
      homeDeliveryCartResult.result.redemptionStatus === AddDiscountCodeStatus.NO_BENEFIT;

    let result: AddDiscountCodeSucceededPayload | undefined;
    if (appliedToCollectAtStoreCart) {
      result = collectAtStoreResult.result;
    } else if (appliedToHomeDeliveryCart) {
      result = homeDeliveryCartResult.result;
    } else if (hasNoBenefitOnCollectAtStoreCart) {
      result = collectAtStoreResult.result;
    } else if (hasNoBenefitOnHomeDeliveryCart) {
      result = homeDeliveryCartResult.result;
    } else if (collectAtStoreResult?.succeeded === true) {
      result = collectAtStoreResult.result;
    } else if (homeDeliveryCartResult?.succeeded === true) {
      result = homeDeliveryCartResult.result;
    }

    return result;
  };

  const successfulResult = getOptionalSuccessfulResult();

  if (successfulResult !== undefined) {
    yield* put(
      addDiscountCodeToCurrentCartsSucceeded({
        requestedActionId,
        discountCode,
        redemptionStatus: successfulResult.redemptionStatus
      })
    );
  } else {
    const failedResult: AddDiscountCodeFailedPayload | undefined =
      collectAtStoreResult?.result ?? homeDeliveryCartResult?.result;
    yield* put(
      addDiscountCodeToCurrentCartsFailed({
        requestedActionId,
        discountCode,
        redemptionStatus: failedResult?.redemptionStatus ?? AddDiscountCodeStatus.NOT_APPLIED
      })
    );
  }
}

export default addDiscountCodeToCurrentCartsSaga;
